import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import ConsumerHeader from "@/components/ConsumerHeader";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-600 mt-2">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Introduction</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our wellness booking platform. Please read this privacy policy carefully.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information We Collect</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-700 leading-relaxed space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Personal Information</h4>
                    <p>We may collect personal information that you voluntarily provide to us when you:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>Register for an account</li>
                      <li>Make a booking</li>
                      <li>Contact us</li>
                      <li>Subscribe to our newsletter</li>
                    </ul>
                    <p className="mt-2">This information may include your name, email address, phone number, and payment information.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Usage Information</h4>
                    <p>We automatically collect certain information when you visit our website, including:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                      <li>IP address and browser type</li>
                      <li>Pages you visit and time spent on our site</li>
                      <li>Device information</li>
                      <li>Location data (if you permit)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How We Use Your Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Provide, operate, and maintain our services</li>
                    <li>Process bookings and transactions</li>
                    <li>Send you booking confirmations and updates</li>
                    <li>Improve our website and services</li>
                    <li>Communicate with you about our services</li>
                    <li>Prevent fraud and ensure security</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information Sharing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>We may share your information with:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>Service Providers:</strong> We share booking information with the wellness providers you book with</li>
                    <li><strong>Payment Processors:</strong> To process your payments securely</li>
                    <li><strong>Service Partners:</strong> Third-party companies that help us operate our platform</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  </ul>
                  <p>We do not sell, trade, or rent your personal information to third parties for marketing purposes.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Security</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cookies and Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    We use cookies and similar tracking technologies to enhance your experience on our website. Cookies help us:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Remember your preferences</li>
                    <li>Analyze website traffic</li>
                    <li>Improve our services</li>
                    <li>Provide personalized content</li>
                  </ul>
                  <p>You can control cookie settings through your browser preferences.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Rights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Access to your personal information</li>
                    <li>Correction of inaccurate information</li>
                    <li>Deletion of your personal information</li>
                    <li>Restriction of processing</li>
                    <li>Data portability</li>
                    <li>Object to processing</li>
                  </ul>
                  <p>To exercise these rights, please contact us using the information provided below.</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Retention</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Children's Privacy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Changes to This Privacy Policy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us through our{" "}
                  <Link to="/contact" className="text-blue-600 hover:text-blue-800 underline">
                    contact page
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src="/lovable-uploads/c9d4d449-ac7c-4868-af9e-bf9b48c9faad.png" 
                  alt="WellSuites" 
                  className="h-6 w-auto brightness-0 invert"
                />
                <button className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2">
                  <img 
                    src="/lovable-uploads/b3af523e-f22c-4fe2-9969-5c45dc7caf3a.png" 
                    alt="Apple" 
                    className="h-4 w-4"
                  />
                  Get the app
                </button>
              </div>
              <p className="text-gray-400 max-w-md">
                Discover and book amazing wellness experiences at top-rated centers near you.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Explore</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Find Suites</a></li>
                <li><a href="#" className="hover:text-white transition-colors">My Account</a></li>
                <li><Link to="/for-business-v2" className="hover:text-white transition-colors">For Business</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About us</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact us</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2025 Wellsuites. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
